import { z } from "zod";

/**
 * What the drafted-test generator is asked to draft from: the dialog's live
 * values, not the saved column.
 *
 * Both configuration pages stage every edit until Save Changes, so a request
 * built from the saved row would draft from old criteria, and would name no
 * row at all for a column or procedure that is still staged. The server adds
 * only what the dialog cannot see (the control or work step, a control's saved
 * key reports and the Optro record), except that a saved procedure drafts on
 * its saved basis; `procedure.basis` is read only for a staged one.
 *
 * Mirrors `DraftTestRequest` in
 * midship/app/api/heretic/controls/analytics_router.py.
 *
 * Optional column fields are omitted rather than sent blank.
 */
export interface DraftedTestRequest {
  /** `null` while the procedure itself is staged and has no row yet. */
  procedureId: string | null;
  column: {
    name?: string;
    passCriteria: string;
    failCriteria?: string;
    naCriteria?: string;
    tolerances?: string;
    expectedEvidences?: string;
    procedureGuidance?: string;
    workflowName?: string;
  };
  attribute: {
    reference: string;
    statement: string;
    description: string;
  };
  procedure: {
    name: string;
    description: string | null;
    basis: "sample" | "direct";
  };
  siblingColumnNames: string[];
  /**
   * The auditor's own text for the new draft to revise: the box's text, or,
   * when the box still holds Midship's last draft unedited, the text that
   * draft revised; null when there is none.
   */
  previousDraft: string | null;
}

// mirrors DatasetNameSource in midship/heretic/analytics/drafted_test_prompt.py
const knownDatasetNameSources = z.enum([
  "key_report",
  "expected_evidence",
  "optro_record",
  "named_by_midship",
]);

// `"unknown"` is a source this build does not know. It is kept rather than
// failing the parse of the whole draft or plan, and shown with no label, since
// any label would claim where the name came from.
export type DatasetNameSource =
  | z.infer<typeof knownDatasetNameSources>
  | "unknown";

const datasetNameSourceSchema = z
  .string()
  .transform((source): DatasetNameSource => {
    const known = knownDatasetNameSources.safeParse(source);
    return known.success ? known.data : "unknown";
  });

/**
 * One dataset a draft reads, by the name the draft uses and where that name
 * came from. The server checks every source before sending it.
 *
 * Mirrors `DraftedTestDataset` in midship/heretic/analytics/drafted_test_prompt.py.
 */
export const draftedTestDatasetSchema = z.object({
  name: z.string(),
  source: datasetNameSourceSchema,
});

export type DraftedTestDataset = z.infer<typeof draftedTestDatasetSchema>;

const DATASET_SOURCE_LABELS: Record<DatasetNameSource, string | null> = {
  key_report: "key report",
  expected_evidence: "Expected evidence",
  optro_record: "Optro record",
  named_by_midship: "named by Midship",
  unknown: null,
};

/**
 * `"Datasets: HR Termination Report (key report) · AD user listing (named by
 * Midship)"`, or `null` when the draft names none.
 */
export function formatDatasetSources(
  datasets: readonly DraftedTestDataset[],
): string | null {
  if (datasets.length === 0) {
    return null;
  }
  const named = datasets.map((dataset) => {
    const label = DATASET_SOURCE_LABELS[dataset.source];
    if (label === null) {
      return dataset.name;
    }
    return `${dataset.name} (${label})`;
  });
  return `Datasets: ${named.join(" · ")}`;
}

/**
 * A generated drafted test and the account of what it was drafted from.
 *
 * Only the fields a surface reads are declared. The response also carries a
 * `structure` breakdown and a `promptVersion` that nothing here renders, and
 * requiring either could only fail a parse and throw the draft away. zod's
 * default strip policy drops them, and `.strict()` is avoided for the reason
 * given in `analyticsColumn.ts`.
 *
 * `namingNudge` is the server's line for a draft whose every dataset Midship
 * named itself, and `null` otherwise.
 *
 * Mirrors `DraftTestResponse` in
 * midship/app/api/heretic/controls/analytics_router.py.
 */
export const draftedTestResponseSchema = z.object({
  draftedTest: z.string(),
  sourcesUsed: z.array(z.string()),
  datasets: z.array(draftedTestDatasetSchema),
  namingNudge: z.string().nullable(),
});

export type DraftedTestResponse = z.infer<typeof draftedTestResponseSchema>;
